import { useState, useEffect, useMemo } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Users, TrendingUp, DollarSign, Activity, AlertTriangle,
  Search, ArrowUpDown, ChevronDown, Crown, Clock, Zap,
  BarChart3, Shield, Eye, UserX, CreditCard, Bell,
  RefreshCw, Loader2, ChevronRight, Monitor, Smartphone,
  BookOpen, ClipboardList, Radio, Sparkles, X
} from 'lucide-react';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

// ─── Helpers ───
function timeAgo(isoStr) {
  if (!isoStr || isoStr === 'never') return 'Never';
  const d = new Date(isoStr);
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function formatDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

const FEATURE_LABELS = {
  schools: 'Schools', classes: 'Classes', students: 'Students',
  attendance: 'Attendance', behaviour: 'Behaviour', live_teaching: 'Live Teaching',
  ai_summaries: 'AI Summaries', timetable: 'Timetable', seating_plans: 'Seating',
  team_generator: 'Teams', earnings: 'Earnings', out_of_class: 'Out of Class',
  whiteboard: 'Whiteboard', bell_times: 'Bell Times', school_notes: 'Notes', other: 'Other',
};

const STATUS_CONFIG = {
  admin: { label: 'Admin', color: 'bg-violet-100 text-violet-700' },
  pro: { label: 'Pro', color: 'bg-emerald-100 text-emerald-700' },
  trial: { label: 'Trial', color: 'bg-blue-100 text-blue-700' },
  expired: { label: 'Expired', color: 'bg-red-100 text-red-700' },
};

// ─── Metric Card ───
function MetricCard({ icon: Icon, label, value, sub, color = 'text-slate-900', iconBg = 'bg-slate-100', iconColor = 'text-slate-600' }) {
  return (
    <Card className="border border-slate-200 hover:border-slate-300 transition-colors" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
            {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
          </div>
          <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center`}>
            <Icon className={`w-4.5 h-4.5 ${iconColor}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Status Badge ───
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, color: 'bg-slate-100 text-slate-600' };
  return <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${cfg.color}`}>{cfg.label}</span>;
}

// ─── Feature Tag ───
function FeatureTag({ name }) {
  return (
    <span className="text-[10px] font-medium bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
      {FEATURE_LABELS[name] || name}
    </span>
  );
}

export const AdminPage = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState('overview');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchAll = async (showToast = false) => {
    setRefreshing(true);
    try {
      const [statsRes, usersRes, activityRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { credentials: 'include' }),
        fetch(`${API}/admin/users`, { credentials: 'include' }),
        fetch(`${API}/admin/activity`, { credentials: 'include' }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (activityRes.ok) setActivity(await activityRes.json());
      if (showToast) toast.success('Dashboard refreshed');
    } catch (e) { toast.error('Failed to load dashboard'); }
    finally { setRefreshing(false); setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  // Auto-refresh activity every 30s
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const r = await fetch(`${API}/admin/activity`, { credentials: 'include' });
        if (r.ok) setActivity(await r.json());
      } catch {}
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const filteredUsers = useMemo(() => {
    let list = users;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(u => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
    }
    if (filter !== 'all') list = list.filter(u => u.status === filter);
    list = [...list].sort((a, b) => {
      if (sortBy === 'newest') return (b.created_at || '').localeCompare(a.created_at || '');
      if (sortBy === 'most-active') return b.total_actions - a.total_actions;
      if (sortBy === 'longest') return b.days_since_signup - a.days_since_signup;
      return 0;
    });
    return list;
  }, [users, search, filter, sortBy]);

  const handleToggleUser = async (userId, disabled) => {
    try {
      const r = await fetch(`${API}/admin/users/${userId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ disabled: !disabled })
      });
      if (r.ok) { toast.success(`User ${disabled ? 'enabled' : 'disabled'}`); fetchAll(); }
    } catch { toast.error('Failed to update user'); }
  };

  const handleGrantPro = async (userId) => {
    try {
      const r = await fetch(`${API}/admin/users/${userId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ is_pro: true, subscription_status: 'subscribed' })
      });
      if (r.ok) { toast.success('User upgraded to Pro'); fetchAll(); }
    } catch { toast.error('Failed to upgrade user'); }
  };

  if (loading) {
    return (
      <Layout breadcrumb={[{ label: 'Admin' }]}>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </Layout>
    );
  }

  const s = stats?.users || {};
  const rev = stats?.revenue || {};
  const usage = stats?.usage || {};
  const alerts = stats?.alerts || {};
  const totalAlerts = (alerts.trials_ending_soon?.length || 0) + (alerts.inactive_users?.length || 0) + (alerts.failed_payments || 0);

  return (
    <Layout breadcrumb={[{ label: 'Admin Dashboard' }]}>
      <div className="space-y-6 max-w-[1400px] mx-auto" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Dashboard</h1>
            <p className="text-sm text-slate-500">Reliefify admin overview</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchAll(true)} disabled={refreshing} className="rounded-lg" data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1 w-fit" data-testid="admin-tabs">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'revenue', label: 'Revenue', icon: DollarSign },
            { id: 'alerts', label: 'Alerts', icon: Bell, count: totalAlerts },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}>
              <t.icon className="w-4 h-4" />
              {t.label}
              {t.count > 0 && (
                <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full ml-1">{t.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* ═══ OVERVIEW TAB ═══ */}
        {tab === 'overview' && (
          <div className="space-y-6">
            {/* Top metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="metrics-grid">
              <MetricCard icon={Users} label="Total Users" value={s.total || 0} sub={`+${s.new_this_week || 0} this week`} iconBg="bg-blue-50" iconColor="text-blue-600" />
              <MetricCard icon={Activity} label="Active 24h" value={s.active_24h || 0} sub={`${s.active_7d || 0} in 7d`} iconBg="bg-emerald-50" iconColor="text-emerald-600" />
              <MetricCard icon={Clock} label="Trial Users" value={s.trial || 0} sub={`${s.expired || 0} expired`} iconBg="bg-amber-50" iconColor="text-amber-600" />
              <MetricCard icon={Crown} label="Pro Users" value={s.pro || 0} iconBg="bg-violet-50" iconColor="text-violet-600" />
              <MetricCard icon={TrendingUp} label="Conversion" value={`${s.conversion_rate || 0}%`} sub="trial → paid" iconBg="bg-teal-50" iconColor="text-teal-600" />
              <MetricCard icon={UserX} label="Churn Rate" value={`${s.churn_rate || 0}%`} iconBg="bg-red-50" iconColor="text-red-500" />
            </div>

            {/* Two columns: Live Activity + Feature Usage */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Live Activity Panel */}
              <Card className="border border-slate-200" data-testid="live-activity">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                      Live Activity
                    </h3>
                    <span className="text-xs text-slate-400">{activity.length} online now</span>
                  </div>
                  {activity.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">No active users right now</p>
                  ) : (
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {activity.map(a => (
                        <div key={a.user_id} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50">
                          <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-xs font-bold text-teal-700">
                            {(a.name || '?')[0]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-800 truncate">{a.name}</p>
                            <p className="text-xs text-slate-500">{FEATURE_LABELS[a.feature] || a.feature}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="flex items-center gap-1">
                              {a.device === 'mobile' ? <Smartphone className="w-3 h-3 text-slate-400" /> : <Monitor className="w-3 h-3 text-slate-400" />}
                            </div>
                            <p className="text-[10px] text-slate-400 mt-0.5">{timeAgo(a.last_timestamp)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Feature Usage */}
              <Card className="border border-slate-200" data-testid="feature-usage">
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-slate-900 mb-4">Feature Usage (30d)</h3>
                  {(stats?.feature_stats || []).length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-8">No usage data yet — will populate as users interact</p>
                  ) : (
                    <div className="space-y-3">
                      {(stats?.feature_stats || []).map((f, i) => {
                        const maxCount = stats.feature_stats[0]?.count || 1;
                        const pct = Math.round((f.count / maxCount) * 100);
                        return (
                          <div key={f.feature}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-slate-700">{FEATURE_LABELS[f.feature] || f.feature}</span>
                              <span className="text-xs font-medium text-slate-500">{f.count}</span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${i === 0 ? 'bg-teal-500' : i < 3 ? 'bg-teal-400' : 'bg-teal-300'}`}
                                style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Platform Usage Stats */}
            <Card className="border border-slate-200" data-testid="usage-stats">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Platform Data</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                  {[
                    { label: 'Schools', value: usage.schools, icon: '🏫' },
                    { label: 'Classes', value: usage.classes, icon: '📚' },
                    { label: 'Students', value: usage.students, icon: '👩‍🎓' },
                    { label: 'Attendance', value: usage.attendance_records, icon: '✅' },
                    { label: 'Behaviour', value: usage.behaviour_logs, icon: '📋' },
                    { label: 'Summaries', value: usage.ai_summaries, icon: '✨' },
                    { label: 'Sessions', value: usage.teaching_sessions, icon: '🎓' },
                  ].map(u => (
                    <div key={u.label} className="text-center p-3 rounded-xl bg-slate-50">
                      <p className="text-2xl mb-1">{u.icon}</p>
                      <p className="text-lg font-bold text-slate-900">{u.value || 0}</p>
                      <p className="text-xs text-slate-500">{u.label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Signups */}
            <Card className="border border-slate-200" data-testid="recent-signups">
              <CardContent className="p-5">
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Recent Signups</h3>
                <div className="space-y-2">
                  {(stats?.recent_signups || []).map((u, i) => (
                    <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700">
                          {(u.name || '?')[0]}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">{u.name}</p>
                          <p className="text-xs text-slate-400">{u.email}</p>
                        </div>
                      </div>
                      <p className="text-xs text-slate-400">{formatDate(u.created_at)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ═══ USERS TAB ═══ */}
        {tab === 'users' && (
          <div className="space-y-4">
            {/* Search & Filters */}
            <div className="flex flex-col sm:flex-row gap-3" data-testid="user-filters">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)}
                  className="pl-10 rounded-lg" data-testid="user-search" />
              </div>
              <div className="flex gap-2">
                {[
                  { id: 'all', label: 'All' },
                  { id: 'trial', label: 'Trial' },
                  { id: 'pro', label: 'Pro' },
                  { id: 'expired', label: 'Expired' },
                ].map(f => (
                  <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`filter-${f.id}`}
                    className={`px-3 py-2 text-xs font-medium rounded-lg transition-colors ${
                      filter === f.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}>{f.label}</button>
                ))}
              </div>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} data-testid="sort-select"
                className="px-3 py-2 text-xs font-medium rounded-lg bg-slate-100 text-slate-600 border-0 cursor-pointer">
                <option value="newest">Newest first</option>
                <option value="most-active">Most active</option>
                <option value="longest">Longest member</option>
              </select>
            </div>

            <p className="text-xs text-slate-400">{filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}</p>

            {/* User Table */}
            <Card className="border border-slate-200 overflow-hidden" data-testid="user-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left">
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">User</th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden md:table-cell">Signup</th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden lg:table-cell">Last Active</th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden lg:table-cell">Actions</th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden xl:table-cell">Features</th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide hidden xl:table-cell">Device</th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Manage</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredUsers.map(u => (
                      <tr key={u.user_id} className="hover:bg-slate-50/50 transition-colors cursor-pointer"
                        onClick={() => setSelectedUser(u)} data-testid={`user-row-${u.user_id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                              u.status === 'admin' ? 'bg-violet-100 text-violet-700' :
                              u.status === 'pro' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                              {(u.name || '?')[0]}
                            </div>
                            <div>
                              <p className="font-medium text-slate-800">{u.name || 'Unnamed'}</p>
                              <p className="text-xs text-slate-400">{u.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={u.status} />
                          {u.status === 'trial' && <p className="text-[10px] text-slate-400 mt-0.5">{u.days_left}d left</p>}
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <p className="text-xs text-slate-600">{formatDate(u.created_at)}</p>
                          <p className="text-[10px] text-slate-400">{u.days_since_signup}d ago</p>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <p className="text-xs text-slate-600">{u.last_active ? timeAgo(u.last_active) : 'Never'}</p>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <p className="text-xs font-medium text-slate-700">{u.total_actions}</p>
                        </td>
                        <td className="px-4 py-3 hidden xl:table-cell">
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {u.features_used.slice(0, 3).map(f => <FeatureTag key={f} name={f} />)}
                            {u.features_used.length > 3 && <span className="text-[10px] text-slate-400">+{u.features_used.length - 3}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden xl:table-cell">
                          <div className="flex gap-1">
                            {u.devices.includes('desktop') && <Monitor className="w-3.5 h-3.5 text-slate-400" />}
                            {u.devices.includes('mobile') && <Smartphone className="w-3.5 h-3.5 text-slate-400" />}
                            {u.devices.length === 0 && <span className="text-xs text-slate-300">-</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <div className="flex gap-1.5">
                            {u.status !== 'admin' && u.status !== 'pro' && (
                              <button onClick={() => handleGrantPro(u.user_id)} title="Grant Pro"
                                className="w-7 h-7 rounded-md bg-emerald-50 hover:bg-emerald-100 flex items-center justify-center text-emerald-600 transition-colors"
                                data-testid={`grant-pro-${u.user_id}`}>
                                <Crown className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {u.status !== 'admin' && (
                              <button onClick={() => handleToggleUser(u.user_id, u.disabled)}
                                title={u.disabled ? 'Enable' : 'Disable'}
                                className={`w-7 h-7 rounded-md flex items-center justify-center transition-colors ${
                                  u.disabled ? 'bg-green-50 hover:bg-green-100 text-green-600' : 'bg-red-50 hover:bg-red-100 text-red-500'
                                }`} data-testid={`toggle-${u.user_id}`}>
                                {u.disabled ? <Eye className="w-3.5 h-3.5" /> : <UserX className="w-3.5 h-3.5" />}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ═══ REVENUE TAB ═══ */}
        {tab === 'revenue' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <MetricCard icon={DollarSign} label="MRR" value={`$${rev.mrr || 0}`} sub="Monthly recurring" iconBg="bg-emerald-50" iconColor="text-emerald-600" color="text-emerald-700" />
              <MetricCard icon={CreditCard} label="Active Subs" value={rev.active_subscriptions || 0} iconBg="bg-blue-50" iconColor="text-blue-600" />
              <MetricCard icon={TrendingUp} label="This Month" value={`$${rev.this_month || 0}`} iconBg="bg-teal-50" iconColor="text-teal-600" />
              <MetricCard icon={AlertTriangle} label="Failed Payments" value={rev.failed_payments || 0} iconBg="bg-red-50" iconColor="text-red-500" color={rev.failed_payments > 0 ? 'text-red-600' : 'text-slate-900'} />
            </div>

            <Card className="border border-slate-200">
              <CardContent className="p-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">Revenue Summary</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div className="text-center p-6 rounded-xl bg-emerald-50">
                    <p className="text-3xl font-bold text-emerald-700">${rev.total || 0}</p>
                    <p className="text-sm text-emerald-600 mt-1">Total Revenue (AUD)</p>
                  </div>
                  <div className="text-center p-6 rounded-xl bg-blue-50">
                    <p className="text-3xl font-bold text-blue-700">{s.conversion_rate || 0}%</p>
                    <p className="text-sm text-blue-600 mt-1">Trial → Paid Conversion</p>
                  </div>
                  <div className="text-center p-6 rounded-xl bg-violet-50">
                    <p className="text-3xl font-bold text-violet-700">${(rev.mrr * 12).toFixed(0) || 0}</p>
                    <p className="text-sm text-violet-600 mt-1">Projected Annual (ARR)</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border border-slate-200">
              <CardContent className="p-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-2">Pricing</h3>
                <p className="text-sm text-slate-500">Current plan: <strong className="text-slate-800">$4.99/month</strong> per user</p>
                <p className="text-sm text-slate-500 mt-1">
                  To reach $500 MRR you need <strong className="text-slate-800">{Math.ceil(500 / 4.99)}</strong> paying users.
                  You currently have <strong className={rev.active_subscriptions > 0 ? 'text-emerald-600' : 'text-slate-800'}>{rev.active_subscriptions || 0}</strong>.
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ═══ ALERTS TAB ═══ */}
        {tab === 'alerts' && (
          <div className="space-y-4">
            {totalAlerts === 0 && (
              <Card className="border border-slate-200">
                <CardContent className="p-8 text-center">
                  <Shield className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">All clear — no alerts right now</p>
                </CardContent>
              </Card>
            )}

            {(alerts.trials_ending_soon || []).length > 0 && (
              <Card className="border border-amber-200 bg-amber-50/30" data-testid="alert-trials">
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-3">
                    <Clock className="w-4 h-4" /> Trials Ending Soon
                  </h3>
                  <div className="space-y-2">
                    {alerts.trials_ending_soon.map((u, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-amber-100 last:border-0">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{u.name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                        <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-1 rounded-full">{u.days_left}d left</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {(alerts.inactive_users || []).length > 0 && (
              <Card className="border border-red-200 bg-red-50/30" data-testid="alert-inactive">
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-red-800 flex items-center gap-2 mb-3">
                    <UserX className="w-4 h-4" /> Inactive Users (7+ days)
                  </h3>
                  <div className="space-y-2">
                    {alerts.inactive_users.map((u, i) => (
                      <div key={i} className="flex items-center justify-between py-2 border-b border-red-100 last:border-0">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{u.name}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                        <span className="text-xs text-red-600">{u.last_active === 'never' ? 'Never active' : `Last: ${timeAgo(u.last_active)}`}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {(alerts.failed_payments || 0) > 0 && (
              <Card className="border border-red-200 bg-red-50/30" data-testid="alert-payments">
                <CardContent className="p-5">
                  <h3 className="text-sm font-semibold text-red-800 flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> {alerts.failed_payments} Failed Payment{alerts.failed_payments > 1 ? 's' : ''}
                  </h3>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ═══ USER DETAIL SLIDE-OVER ═══ */}
        {selectedUser && (
          <div className="fixed inset-0 z-50 flex justify-end" data-testid="user-detail-panel">
            <div className="absolute inset-0 bg-black/30" onClick={() => setSelectedUser(null)} />
            <div className="relative w-full max-w-md bg-white shadow-2xl overflow-y-auto animate-slide-in-right">
              <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-center justify-between z-10">
                <h3 className="font-semibold text-slate-900">User Details</h3>
                <button onClick={() => setSelectedUser(null)} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-5 space-y-5">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-bold ${
                    selectedUser.status === 'admin' ? 'bg-violet-100 text-violet-700' :
                    selectedUser.status === 'pro' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {(selectedUser.name || '?')[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-lg">{selectedUser.name}</p>
                    <p className="text-sm text-slate-500">{selectedUser.email}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <StatusBadge status={selectedUser.status} />
                  {selectedUser.disabled && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Disabled</span>}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Signed Up', value: formatDate(selectedUser.created_at) },
                    { label: 'Days as User', value: `${selectedUser.days_since_signup}d` },
                    { label: 'Last Active', value: selectedUser.last_active ? timeAgo(selectedUser.last_active) : 'Never' },
                    { label: 'Total Actions', value: selectedUser.total_actions },
                    { label: 'Trial Expires', value: selectedUser.trial_expiry ? formatDate(selectedUser.trial_expiry) : '-' },
                    { label: 'Days Left', value: selectedUser.status === 'admin' ? 'Unlimited' : `${selectedUser.days_left}d` },
                    { label: 'Classes', value: selectedUser.class_count },
                    { label: 'Device', value: selectedUser.devices.join(', ') || '-' },
                  ].map(d => (
                    <div key={d.label} className="bg-slate-50 rounded-lg p-3">
                      <p className="text-[10px] font-medium text-slate-400 uppercase">{d.label}</p>
                      <p className="text-sm font-semibold text-slate-800 mt-0.5">{d.value}</p>
                    </div>
                  ))}
                </div>
                {selectedUser.features_used.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-slate-400 uppercase mb-2">Features Used</p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedUser.features_used.map(f => <FeatureTag key={f} name={f} />)}
                    </div>
                  </div>
                )}
                {selectedUser.status !== 'admin' && (
                  <div className="pt-3 border-t border-slate-100 flex gap-2">
                    {!selectedUser.is_pro && (
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg" onClick={() => { handleGrantPro(selectedUser.user_id); setSelectedUser(null); }}>
                        <Crown className="w-3.5 h-3.5 mr-1.5" /> Grant Pro
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => { handleToggleUser(selectedUser.user_id, selectedUser.disabled); setSelectedUser(null); }}>
                      {selectedUser.disabled ? 'Enable User' : 'Disable User'}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
